import { useEffect, useState, useRef } from "react";
import { Typography, Link } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Lottie from "react-lottie";
import { useMutation, useQuery } from "@apollo/client";
import { Box } from "@mui/system";
import ContentShimmer from "react-content-shimmer";

import AppButton from "../../base/AppButton.component";
import VerificationLayout from "../../layout/VerificationLayout.component";
import ConsentGif from "../../../assets/gifs/consent-security.json";
import {
  firstOfMonth,
  lottieLoopOptions,
  teamID,
  today,
  TYPE,
} from "../../../utils/Utils";
import { useVerification } from "../../../context/Verification.context";
import DocumentDialog from "../insurance/dialog/dialogBody/Document.dialog";
import HocPopup from "../../../hoc/hocPopup";
import { useAppContext } from "../../../context/App.context";
import Theme from "../../../utils/Theme";
import {
  CONSENT_CONNECT,
  LOG_KNOWLEDGEHUB_OPEN,
} from "../../../graphql/mutation/mutation";
import InformationDialog from "../../base/dialog-body/Information.dialog";
import {
  CONSENT_DATAS,
  FETCH_CUSTOMERS,
  INSURANCE_CARRIERS,
} from "../../../graphql/query/query";
import WalkthroughDialog from "./WalkthroughDialog";
import { shouldIncludeInsuranceWalkthrough } from "../../../utils/videoPlayer";

const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    console.error("JSON.parse failed:", e, "\nInput:", str);
    return null;
  }
};

// Must match the channel values KnowledgeHubComponent appends to the
// consent URL (?channel=...) in emailhandler / textHandler / "Continue here".
// Also used for the ?via= param added by the Verify Insurance dialog
// (GetUserDetails) — same values: email / text / device.
const VALID_KH_CHANNELS = ["email", "text", "device"];

const ConsentComponent = ({ dialog }) => {
  const [showInsuranceWalkthrough, setShowInsuranceWalkthrough] =
    useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const verification = useVerification();
  const app = useAppContext();
  lottieLoopOptions.animationData = ConsentGif;

  const [searchParams] = useSearchParams();
  const services = searchParams.getAll("service");
  const fct = searchParams.get("fct");
  const expressLogin = searchParams.get("express") === "true";

  const hasLoggedKhOpen = useRef(false);
  const [logKnowledgeHubOpen] = useMutation(LOG_KNOWLEDGEHUB_OPEN);

  // Prefer URL ?service=; else dealership services (dashboard KPI has no ?service=);
  // do not let a stale customer.serviceId of ["1"] block Knowledge Hub.
  const getActiveServiceIds = (dealershipServices) => {
    const fromUrl = (services || [])
      .map(String)
      .filter((id) => id === "1" || id === "2");

    const fromDealership = [];
    if (dealershipServices?.length) {
      dealershipServices.forEach((item) => {
        if (item?.service_id === 1) fromDealership.push("1");
        if (item?.service_id === 2) fromDealership.push("2");
      });
    }

    // URL path: only keep services the dealer has enabled
    if (fromUrl.length) {
      if (!fromDealership.length) return fromUrl;
      return fromUrl.filter((id) => fromDealership.includes(id));
    }

    if (fromDealership.length) return fromDealership;

    const fromCustomer = (verification?.customer?.serviceId || [])
      .map(String)
      .filter((id) => id === "1" || id === "2");
    if (fromCustomer.length) return fromCustomer;
    return ["1"];
  };

  useEffect(() => {
    verification.setEmployeeId(params.spid);
    verification.setDealershipId(params.dealershipId);
    verification.setCustomer({
      ...verification.customer,
      transactionId: Date.now().toString(),
      serviceId: getActiveServiceIds(),
      // isOmniUser: params?.isOmniUser ? params?.isOmniUser : false,
    });
    // Reset skip flags from THIS URL (session may still have express/fct from a prior visit)
    verification.setIsFromCustomerTable(fct === "true");
    verification.setIsExpressLogin(expressLogin);
    // Insurance-only SERVICE is URL-only (?service=1, no service=2) — not dashboard
    const urlServices = (services || [])
      .map(String)
      .filter((id) => id === "1" || id === "2");
    verification.setIsInsuranceOnlyService(
      urlServices.includes("1") && !urlServices.includes("2"),
    );

    // Channel chosen in the Verify Insurance dialog (?via=email|text|device).
    // Stored on context so ThankYou can log it when the Knowledge Hub starts.
    // Also clears any stale value from a previous visit.
    // Links that carry ?channel= are already logged at Consent (below), so
    // they must not be logged a second time on the thank-you screen.
    const via = searchParams.get("via");
    const hasKhChannelParam = VALID_KH_CHANNELS.includes(
      searchParams.get("channel"),
    );
    verification.setKhChannel(
      !hasKhChannelParam && VALID_KH_CHANNELS.includes(via) ? via : null,
    );

    app.setFetchData({
      d_startDate: firstOfMonth,
      d_endDate: today,
      f_startDate: firstOfMonth,
      f_endDate: today,
      pageCount: params?.pageCount || 0,
      rowsPerPage: 20,
      searchEmployee: "",
      searchCustomer: "",
    });
  }, []);

  // Log Knowledge Hub open — fires once per mount when the consent URL carries
  // a ?channel= param (added by KnowledgeHubComponent's emailhandler /
  // textHandler / "Continue here" button). No-ops for consent links that
  // don't originate from Knowledge Hub (no channel param present).
  useEffect(() => {
    const channel = searchParams.get("channel");
    if (!channel || !VALID_KH_CHANNELS.includes(channel)) return;
    if (hasLoggedKhOpen.current) return;
    hasLoggedKhOpen.current = true;

    logKnowledgeHubOpen({
      variables: {
        input: {
          dealership_id: parseInt(params.dealershipId),
          employee_user_id: parseInt(params.spid),
          user_id: parseInt(params.customer),
          channel,
        },
      },
    }).catch((err) => {
      console.error("Failed to log Knowledge Hub open:", err);
    });
  }, []);

  useEffect(() => {
    verification.setIsWalkthrough(params.isWalkthrough === "true");
    // Each consent visit can show Insurance WT again before carrier
    if (params.isWalkthrough === "true") {
      verification.setHasSeenInsuranceWalkthrough(false);
    }
  }, [params.isWalkthrough]);
  useEffect(() => {
    verification.setFetchData({
      d_startDate: firstOfMonth,
      d_endDate: today,
      f_startDate: firstOfMonth,
      f_endDate: today,
      pageCount: parseInt(params?.pageCount) || 0,
      rowsPerPage: 20,
      searchEmployee: "",
      searchCustomer: "",
    });
  }, [params.isRepull]);

  const { data, loading } = useQuery(CONSENT_DATAS, {
    variables: {
      verificationInput: {
        customer_id: parseInt(params?.customer),
        employee_user_id: parseInt(params?.spid),
        dealership_id: parseInt(params?.dealershipId),
      },
    },
  });

  const isRepull = params?.isRepull === "true" || params?.isRepull === true;
  const { data: fetchCustomers, loading: FetchLoading } = useQuery(
    FETCH_CUSTOMERS,
    {
      variables: {
        fetchCustomerInput: {
          user_id: parseInt(
            isRepull ? params?.customer : verification?.customer?.userId
          ) || parseInt(verification?.customer?.userId),
          ...(isRepull &&
            params?.spid != null &&
            params?.dealershipId != null && {
              employee_user_id: parseInt(params.spid),
              dealership_id: parseInt(params.dealershipId),
            }),
        },
      },
      skip: !(isRepull ? params?.customer : verification?.customer?.userId),
    }
  );

  useEffect(() => {
    if (data?.consentDatas?.verification_url) {
      const response = data?.consentDatas;
      let verificationUrl = safeJsonParse(response?.verification_url);
      const activeServiceIds = getActiveServiceIds(
        data?.consentDatas?.dealershipServices,
      );
      verification.setCustomer({
        ...verification.customer,
        userId: response.userId,
        firstName: verificationUrl?.firstName,
        lastName: verificationUrl?.lastName,
        email: verificationUrl?.email,
        mobileNumber: verificationUrl?.mobileNumber,
        vin: verificationUrl?.vin,
        // URL services win; otherwise dealership services (incl. KH for dashboard Verify Only)
        serviceId: activeServiceIds,
      });
      verification.setWorkflow(safeJsonParse(response?.verification_workflow_id));
      verification.setPercentage(safeJsonParse(response?.percentage));
      verification.setDealershipLogo(response?.image_url);
      verification.setOpeningHours(response?.dealershipTiming);

          if (response?.walkthrough_video) {
      verification.setWalkthroughVideoUrl(response.walkthrough_video);
    }
      data?.consentDatas?.dealershipServices?.forEach((item) => {
        if (item?.service_id === 1) {
          verification.setCheckService((prev) => ({
            ...prev,
            insurance: true,
          }));
        } else if (item?.service_id === 2) {
          verification.setCheckService((prev) => ({
            ...prev,
            knowledgeHub: true,
          }));
        } else if (item?.service_id === 3) {
          verification.setCheckService((prev) => ({
            ...prev,
            prequal: true,
          }));
        } else if (item?.service_id === 4) {
          verification.setCheckService((prev) => ({
            ...prev,
            idVerfication: true,
          }));
        }
      });

      if (params?.isRepull) {
        verification.setIsRepull(params?.isRepull);
      } else verification.setIsRepull(null);

      if (fct === "true") {
        verification.setIsFromCustomerTable(true);
      } else {
        verification.setIsFromCustomerTable(false);
      }

      // Mark as Express Login device flow so downstream screens can skip
      // the Scanning and Tick pages and go directly to Trade & Quote.
      if (expressLogin) {
        verification.setIsExpressLogin(true);
      } else {
        verification.setIsExpressLogin(false);
      }
    }
  }, [data, fct]);

  const { data: carrierData, loading: carrierLoading } = useQuery(INSURANCE_CARRIERS, {
    variables: {
      carrierInput: {
        transaction_id: verification?.customer?.transactionId,
        first_name: fetchCustomers?.fetchCustomerDetails?.first_name,
        last_name: fetchCustomers?.fetchCustomerDetails?.last_name,
        email: fetchCustomers?.fetchCustomerDetails?.email,
        mobile_number: fetchCustomers?.fetchCustomerDetails?.mobile_number,
        vin: fetchCustomers?.fetchCustomerDetails?.vin,
        employee_user_id: parseInt(verification?.employeeId),
        user_id: verification?.customer?.userId,
        dealership_id: parseInt(verification?.dealershipId),
      },
    },
    skip:
      !verification?.customer?.transactionId ||
      !params?.isRepull ||
      !fetchCustomers?.fetchCustomerDetails,
  });

  const repullCarrierReady =
    !isRepull || (!!verification?.insuranceObject?.carrier_id && !carrierLoading);

  useEffect(() => {
    if (
      (verification?.isRepull === true || verification?.isRepull === "true") &&
      fetchCustomers &&
      carrierData?.insuranceCarrier?.Carrier.length > 0
    ) {
      verification.setCustomer({
        ...verification.customer,
        repullCustomer: fetchCustomers?.fetchCustomerDetails,
      });

      let getCarrierName = carrierData?.insuranceCarrier?.Carrier.find(
        (carrier) =>
          carrier?.carrier_id ===
          fetchCustomers?.fetchCustomerDetails?.insurance_carrier
      );

      verification.setInsuranceProvider(
        JSON.stringify({ provider: getCarrierName })
      );
    }
  }, [
    verification?.isRepull,
    fetchCustomers,
    carrierData?.insuranceCarrier?.Carrier.length > 0,
  ]);

  const [request, { loading: consentLoading }] = useMutation(CONSENT_CONNECT, {
    onError: (err) => {
      console.log(err);
    },
    onCompleted: (data) => {
      if (data) {
        if (data.consentAndConnect.errors) {
          const errorMsg =
            data.consentAndConnect.errors[0]?.message ||
            "Something went wrong. Please try again.";
          dialog({
            open: true,
            bodyType: TYPE.DEFAULT,
            title: "Error",
            body: (
              <InformationDialog
                text={errorMsg}
              />
            ),
          });
        } else if (data.consentAndConnect.pull_id) {
          const insuranceInput = {
            employee_user_id: parseInt(verification.employeeId),
            device_identifier: "davi",
            terms_version: 1,
            consent_language:
              "By clicking <name of button> you agree to the Canopy Connect End User Privacy Policy and Terms of Service",
            public_alias: teamID,
            insurer_name: `${verification.insuranceObject?.carrier_id}`,
            insurer_username: fetchCustomers?.fetchCustomerDetails?.carrier_userid,
            insurer_password: fetchCustomers?.fetchCustomerDetails?.carrier_password,
            insurer_thirdField: fetchCustomers?.fetchCustomerDetails?.zipcode,
            transaction_id: verification?.customer?.transactionId,
            customer: {
              user_id: parseInt(verification?.customer?.userId),
              first_name: fetchCustomers?.fetchCustomerDetails?.first_name,
              last_name: fetchCustomers?.fetchCustomerDetails?.last_name,
              email: fetchCustomers?.fetchCustomerDetails?.email,
              mobile_number: fetchCustomers?.fetchCustomerDetails?.mobile_number,
              vin: fetchCustomers?.fetchCustomerDetails?.vin,
            },
            dealership_id: parseInt(verification?.dealershipId),
            pull_id: data.consentAndConnect.pull_id,
            pull_jwt: data.consentAndConnect.pull_jwt,
          };
          verification.setLastConsentInsuranceInput?.(insuranceInput);
          verification.setPullInsurance({
            pullId: data.consentAndConnect.pull_id,
            pullJwt: data.consentAndConnect.pull_jwt,
          });
          navigate(`/verification/verifyInsurance`, { replace: true });
        } else {
          console.log("error", data);
        }
      }
    },
  });
  const handleLogin = () => {
    request({
      variables: {
        insuranceInput: {
          employee_user_id: parseInt(verification.employeeId),
          device_identifier: "davi",
          terms_version: 1,
          consent_language:
            "By clicking <name of button> you agree to the Canopy Connect End User Privacy Policy and Terms of Service",
          public_alias: teamID,
          insurer_name: `${verification.insuranceObject?.carrier_id}`,
          insurer_username:
            fetchCustomers?.fetchCustomerDetails?.carrier_userid,
          insurer_password:
            fetchCustomers?.fetchCustomerDetails?.carrier_password,
          insurer_thirdField: fetchCustomers?.fetchCustomerDetails?.zipcode,
          transaction_id: verification?.customer?.transactionId,
          customer: {
            user_id: parseInt(verification?.customer?.userId),
            first_name: fetchCustomers?.fetchCustomerDetails?.first_name,
            last_name: fetchCustomers?.fetchCustomerDetails?.last_name,
            email: fetchCustomers?.fetchCustomerDetails?.email,
            mobile_number: fetchCustomers?.fetchCustomerDetails?.mobile_number,
            vin: fetchCustomers?.fetchCustomerDetails?.vin,
          },
          dealership_id: parseInt(verification?.dealershipId),
        },
      },
    });
  };
  // Flow:
  // 1) Combined / Continue Delivery:
  //    Consent → IW → carrier → [verify | continue] → requestQuote/thankYou
  //    → (if artifacts) KW → artifacts
  // 2) Insurance only: Consent → Insurance WT (full-page) → carrier → insurance → thank you
  // 3) KH only: Consent → knowledgeHub (KH WT → artifacts)
  const navigateAfterConsent = () => {
    const ids = getActiveServiceIds();
    if (ids.includes("1")) {
      navigate(`/verification/selectProvider`, { replace: true });
    } else if (ids.includes("2")) {
      navigate(`/verification/knowledgeHub`, { replace: true });
    } else {
      navigate(`/verification/selectProvider`, { replace: true });
    }
  };

  const isInsuranceServiceFlow = () => getActiveServiceIds().includes("1");

  const handleService = () => {
    // Prefer live consent payload if context walkthrough URL is not set yet
    const walkthroughSource =
      data?.consentDatas?.walkthrough_video ||
      verification?.walkthroughVideoUrl ||
      null;

    const hasInsuranceWtContent = !!(
      walkthroughSource &&
      ((typeof walkthroughSource.video_url === "string" &&
        walkthroughSource.video_url.trim()) ||
        (typeof walkthroughSource.es_video_url === "string" &&
          walkthroughSource.es_video_url.trim()) ||
        (typeof walkthroughSource.text === "string" &&
          walkthroughSource.text.trim()) ||
        (typeof walkthroughSource.es_text === "string" &&
          walkthroughSource.es_text.trim()))
    );

    // Enable WT when URL says so OR dealership has insurance WT content
    // (some consent links omit :isWalkthrough and would otherwise skip forever)
    const walkthroughEnabled =
      verification?.isWalkthrough ||
      params?.isWalkthrough === "true" ||
      hasInsuranceWtContent;

    const canShowInsuranceWt = shouldIncludeInsuranceWalkthrough({
      ...verification,
      isWalkthrough: walkthroughEnabled,
      walkthroughVideoUrl: walkthroughSource,
      hasSeenInsuranceWalkthrough: false, // always allow from Consent NEXT
      // Use THIS visit's URL flags — session may still have express/fct from earlier
      isRepull: params?.isRepull === "true" || params?.isRepull === true,
      isFromCustomerTable: fct === "true",
      isExpressLogin: expressLogin,
    });

    // Insurance Walkthrough as full-page Consent step before carrier
    if (isInsuranceServiceFlow() && canShowInsuranceWt) {
      if (walkthroughSource) {
        verification.setWalkthroughVideoUrl(walkthroughSource);
      }
      verification.setHasSeenInsuranceWalkthrough(false);
      setShowInsuranceWalkthrough(true);
      return;
    }
    navigateAfterConsent();
  };

  const handleInsuranceWalkthroughClose = () => {
    setShowInsuranceWalkthrough(false);
    verification.setHasSeenInsuranceWalkthrough(true);
    navigateAfterConsent();
  };

  // Insurance WT as full-page step inside Consent (no popup Dialog)
  if (showInsuranceWalkthrough) {
    return (
      <VerificationLayout>
        <WalkthroughDialog
          videoUrl={
            data?.consentDatas?.walkthrough_video ||
            verification?.walkthroughVideoUrl
          }
          handleClose={handleInsuranceWalkthroughClose}
          open={showInsuranceWalkthrough}
          isKnowledgeHub={false}
          inline
        />
      </VerificationLayout>
    );
  }

  return (
    <VerificationLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          // flex: "20%",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={350}
        >
          {!verification?.dealershipLogo ? (
            <ContentShimmer
              size={{
                height: window.innerWidth <= 750 ? 30 : 60,
                width: window.innerWidth <= 750 ? 90 : 150,
              }}
            />
          ) : (
            <img
              src={verification?.dealershipLogo}
              width={150}
              height={60}
              // alt="Dealership Logo"
              style={{
                objectFit: "contain",
              }}
            />
          )}
        </Box>

        <Typography variant="h6" align="center" style={{ marginTop: "26px" }}>
          We value your time and security. Once you provide your carrier's
          credentials, the verification typically takes less than 60 seconds
        </Typography>
      </Box>
      <Box
        sx={{
          // flex: "60%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Lottie
          options={lottieLoopOptions}
          height={
            window.innerHeight >= 1440
              ? 270
              : window.innerHeight >= 1050
              ? 270
              : window.innerHeight >= 900
              ? 200
              : 150
          }
          width={
            window.innerHeight >= 1440
              ? 270
              : window.innerHeight >= 1050
              ? 270
              : window.innerHeight >= 900
              ? 200
              : 150
          }
        />
        <Box>
          <Typography
            variant="h5"
            fontWeight={"bold"}
            align="center"
            style={{ marginTop: "12px" }}
          >
            SECURE & PRIVATE
          </Typography>
          <Typography align="center" style={{ marginTop: "12px" }}>
            Transfer of your information is encrypted end-to-end and your
            credentials will never be made available to your dealership
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          // flex: "20%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          // mt: window.innerWidth <= 750 ? "0px" : "95px",
        }}
      >
        <AppButton
          buttonText="NEXT"
          sx={{ m: 1, marginTop: "26px", width: "50%" }}
          clickHandler={() => {
            if (
              (params?.isRepull === "true" || params?.isRepull === true) &&
              fetchCustomers?.fetchCustomerDetails?.carrier_userid
            ) {
              if (!repullCarrierReady) {
                dialog({
                  open: true,
                  bodyType: TYPE.DEFAULT,
                  title: "Please wait",
                  body: (
                    <InformationDialog text="Loading your saved insurance provider. Please try again in a moment." />
                  ),
                });
                return;
              }
              handleLogin();
            } else {
              handleService();
            }
          }}
          loading={params?.isRepull ? consentLoading : null}
          disabled={
            !verification?.customer?.userId ||
            loading ||
            FetchLoading ||
            (isRepull &&
              fetchCustomers?.fetchCustomerDetails?.carrier_userid &&
              !repullCarrierReady)
          }
        />
        <Typography
          align="center"
          fontSize={"12px"}
          style={{ padding: "12px" }}
        >
          *By selecting “NEXT” you agree to the eCheckInsurance.com end{" "}
          <Link
            href="javascript:;"
            sx={{ color: "primary" }}
            onClick={() =>
              dialog({
                open: true,
                bodyType: TYPE.PRIVACY_POLICY,
                maxWidth: "md",
                body: (
                  <DocumentDialog
                    url={
                      "https://s3.us-east-2.amazonaws.com/echeck.dev/app/documents/echeck-privacy-policy.pdf"
                    }
                    showPrintButton={false}
                  />
                ),
              })
            }
          >
            end user policy{" "}
          </Link>
          and{" "}
          <Link
            href="javascript:;"
            sx={{ color: Theme.palette.primary.main }}
            onClick={() =>
              dialog({
                open: true,
                bodyType: TYPE.USER_TERMS,
                maxWidth: "md",
                body: (
                  <DocumentDialog
                    url={
                      "https://s3.us-east-2.amazonaws.com/echeck.dev/app/documents/echeck-terms-of-use.pdf"
                    }
                    showPrintButton={false}
                  />
                ),
              })
            }
          >
            user terms
          </Link>{" "}
          If submitting on behalf of a consumer, you confirm their consent to
          user policy, user terms, and to receive texts or emails.
        </Typography>
      </Box>
    </VerificationLayout>
  );
};
export default HocPopup(ConsentComponent);